import moment from 'moment-timezone';
import Request from 'files/Request.js';
import Utils from 'files/Utils.js';

class EmergencyTriggerClass {

    date = null;
    id = null;
    status = null;
    subscription_id = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.date = props.date && moment.utc(props.date).local();
        this.id = props.id;
        this.initiated_by_trusted_contact_id = props.initiated_by_trusted_contact_id;
        this.status = props.status;
        this.subscription_id = props.subscription_id;
        this.user_id = props.user_id;
        return this;
    }
}

class LifeCheckClass {

    completion_date = null;
    date = null;
    failure_date = null;
    id = null;
    is_emergency_override = null;
    status = null;
    subscription_id = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.completion_date = props.completion_date && moment.utc(props.completion_date).local();
        this.date = props.date && moment.utc(props.date).local();
        this.failure_date = props.failure_date && moment.utc(props.failure_date).local();
        this.id = props.id;
        this.is_emergency_override = props.is_emergency_override;
        this.status = props.status;
        this.subscription_id = props.subscription_id;
        this.user_id = props.user_id;
        return this;
    }
}

class SubscriptionClass {

    active = null;
    costs = null;
    created = null;
    flags = [];
    id = null;
    is_activated = null;
    pause_until_date = null;
    payment_method_id = null;
    preferences = null;
    promotions = null;
    renewal_date = null;
    schedule = null;
    tier = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {

        this.active = props.active;
        this.costs = props.costs;
        this.created = props.created && moment.utc(props.created).local();
        this.flags = props.flags;
        this.id = props.id;
        this.is_activated = props.is_activated;
        this.pause_until_date = props.pause_until_date ? moment.utc(props.pause_until_date).local() : null;
        this.payment_method_id = props.payment_method_id;
        this.preferences = props.preferences;
        this.promotions = props.promotions;
        this.renewal_date = props.renewal_date && moment.utc(props.renewal_date).local();
        this.schedule = props.schedule;
        this.tier = props.tier;
        this.user_id = props.user_id;
        return this;
    }
}

const promotionCategories = {
    fixed: 1,
    percentage: 2,
    replace: 3,
    service: 4
}

const promotionOverview = promotion => {

    // create a friendly label to represent the discount amount
    let label = null;
    switch(promotion.category) {
        case promotionCategories.fixed:
        label = `${Utils.toCurrency(promotion.amount)} discount`;
        break;

        case promotionCategories.percentage:
        let percent = promotion.amount * 100;
        label = `${percent % 1 === 0 ? parseInt(percent) : percent.toFixed(2)}% discount`;
        break;

        case promotionCategories.replace:
        label = Utils.toCurrency(promotion.amount);
        break;

        case promotionCategories.service:
        label = `${Utils.toCurrency(promotion.amount)} Service`;
        break;
    }

    // no additional logic is required if no end date was set
    if(!promotion.end_date) {
        return label;
    }

    // add expiration date to result
    let end = moment(promotion.end_date);
    return `${label} until ${end.format('MMM Do, YYYY')}`
    
}

const schedules = {
    daily: 1,
    weekly: 2,
    bi_weekly: 3,
    monthly: 4,
    quarterly: 5,
    annually: 6,
    three_year: 7,
    five_year: 8,
    seven_year: 9,
    ten_year: 10
}

const scheduleToText = (code, plural = true) => {
    switch(code) {
        case schedules.daily:
        return 'Daily';

        case schedules.weekly:
        return 'Weekly';

        case schedules.bi_weekly:
        return 'Bi-Weekly';

        case schedules.monthly:
        return 'Monthly';

        case schedules.quarterly:
        return 'Quarterly';

        case schedules.annually:
        return 'Annually';

        case schedules.three_year:
        return plural ? '3 Years' : '3 Year';

        case schedules.five_year:
        return plural ? '5 Years' : '5 Year';

        case schedules.seven_year:
        return plural ? '7 Years' : '7 Year';

        case schedules.ten_year:
        return plural ? '10 Years' : '10 Year';

        default:
        return 'Unknown schedule type';
    }
}

const validatePromotions = (result, promotions) => {
    return new Promise((resolve, reject) => {
        try {

            // loop through promotions and look for a conflict
            promotions.forEach(promotion => {

                // skip promotion if the promotion matches the promotion that is being validated
                // this ensures that promotions can be edited without their previous states being used for validations
                if(result.id === promotion.id) {
                    return;
                }
                    
                // check if promotion represents an open ended date range and if the new dates conflict
                if(!promotion.end_date) {
                    if(result.start_date >= promotion.start_date || (result.end_date && result.end_date >= promotion.start_date)) {
                        throw new Error(`Your promotion dates conflict with the open ended "${promotion.title}" promotion. Please start and end your promotion before ${promotion.start_date.format('MMMM Do, YYYY')} or change the dates for the "${promotion.title}" promotion.`);
                    }
                    return;
                }

                // check if new start date falls within promotion date range
                if(result.start_date >= promotion.start_date && result.start_date <= promotion.end_date) {
                    throw new Error(`Your promotion start date conflicts with the "${promotion.title}" promotion. Please start and end your promotion before ${promotion.start_date.format('MMMM Do, YYYY')} or after ${promotion.end_date.format('MMMM Do, YYYY')}.`);
                }

                // check if new start date falls within promotion date range
                if(result.end_date >= promotion.start_date && result.end_date <= promotion.end_date) {
                    throw new Error(`Your promotion end date conflicts with the "${promotion.title}" promotion. Please end your promotion before ${promotion.start_date.format('MMMM Do, YYYY')}.`);
                }

                // check if requested date range overlaps this promotion, this can happen if the promotion date range is completely inside the requsted date range
                if((promotion.start_date >= result.start_date && promotion.start_date <= result.end_datae) || (promotion.end_date >= result.start_date && promotion.end_date <= result.end_date)) {
                    throw new Error(`Your promotion overlaps with "${promotion.title}" promotion from ${promotion.start_date.format('MMMM Do, YYYY')} to ${promotion.end_date.format('MMMM Do, YYYY')}`);
                }
            });
            resolve();

        } catch(e) {
            reject(e);
        }
    });
}

export const fetchSubscription = async (utils, id) => {
    return new Promise(async (resolve, reject) => {
        try {
            let { subscription } = await Request.get(utils, '/subscriptions/', {
                id: id,
                type: 'details'
            });
            resolve(new SubscriptionClass().create(subscription));
        } catch(e){
            reject(e);
        }
    });
}

export default {
    create: props => new SubscriptionClass().create(props),
    EmergencyTrigger: {
        create: props => new EmergencyTriggerClass().create(props),
    },
    get: fetchSubscription,
    LifeCheck: {
        create: props => new LifeCheckClass().create(props),
    },
    promotions: {
        categories: {
            get: () => promotionCategories
        },
        overview: promotionOverview,
        validate: validatePromotions
    },
    schedules: {
        get: () => schedules,
        toText: scheduleToText
    }
}
